import React, { useState } from 'react';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { Checkbox, CheckboxChangeEvent } from '@progress/kendo-react-inputs';
import { TextArea } from '@progress/kendo-react-inputs';
import { Button } from '@progress/kendo-react-buttons';
import { PostedAppUnlockSections, PostedAppUnlockSection } from '../../../utils/constants';

interface UnlockDialogProps {
  applicationId: string;
  visible: boolean;
  onClose: () => void;
  onUnlock: (applicationId: string, sections: string[], reason?: string) => void;
}

const sectionLabels: Record<PostedAppUnlockSection, string> = {
  [PostedAppUnlockSections.Vehicle]: 'Vehicle Information',
  [PostedAppUnlockSections.Budget]: 'Budget / Deal Structure',
  [PostedAppUnlockSections.Quoter]: 'Quoter (Rate, Term, Payment, Fees)',
  [PostedAppUnlockSections.CustomerDetails]: 'Customer Details (Address, Phone, SSN, DOB, Employment)',
  [PostedAppUnlockSections.CustomFields]: 'Custom Fields',
  [PostedAppUnlockSections.Status]: 'Application Status',
  [PostedAppUnlockSections.Assignments]: 'Employee Assignments',
};

const UnlockDialog: React.FC<UnlockDialogProps> = ({
  applicationId,
  visible,
  onClose,
  onUnlock,
}) => {
  const [selectedSections, setSelectedSections] = useState<Set<string>>(new Set());
  const [reason, setReason] = useState('');

  const handleCheckboxChange = (sectionKey: string) => (e: CheckboxChangeEvent) => {
    setSelectedSections(prev => {
      const next = new Set(prev);
      if (e.value) {
        next.add(sectionKey);
      } else {
        next.delete(sectionKey);
      }
      return next;
    });
  };

  const handleUnlock = () => {
    if (selectedSections.size === 0) return;
    onUnlock(applicationId, Array.from(selectedSections), reason || undefined);
    setSelectedSections(new Set());
    setReason('');
    onClose();
  };

  const handleClose = () => {
    setSelectedSections(new Set());
    setReason('');
    onClose();
  };

  if (!visible) return null;

  return (
    <Dialog title="Unlock Application Sections" onClose={handleClose} width={500}>
      <div style={{ padding: '10px 0' }}>
        <p style={{ marginBottom: 16, color: '#666' }}>
          This application is locked. Select the sections you need to edit and provide an optional reason.
          Sections will re-lock automatically when you save.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
          {Object.entries(sectionLabels).map(([key, label]) => (
            <Checkbox
              key={key}
              label={label}
              value={selectedSections.has(key)}
              onChange={handleCheckboxChange(key)}
            />
          ))}
        </div>

        <div style={{ marginBottom: 8 }}>
          <label style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>
            Reason (optional)
          </label>
          <TextArea
            value={reason}
            onChange={(e) => setReason(e.value ?? '')}
            rows={3}
            style={{ width: '100%' }}
            placeholder="e.g., Correcting VIN typo"
          />
        </div>
      </div>

      <DialogActionsBar>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          themeColor="primary"
          disabled={selectedSections.size === 0}
          onClick={handleUnlock}
        >
          Unlock Selected Sections
        </Button>
      </DialogActionsBar>
    </Dialog>
  );
};

export default UnlockDialog;
